/* eslint-disable camelcase */

import React, { useContext, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  HvGlobalActions,
  HvGrid,
  HvTypography,
} from "@hitachivantara/uikit-react-core";

import {
  useHvTable,
  useHvPagination,
  useHvSortBy,
  HvTableColumnConfig,
} from "@hitachivantara/uikit-react-lab";

import { IncidentContext } from "lib/context/IncidentContext";
import SimpleTable, {
  dateColumn,
  DEFAULT_PAGE,
  textColumn,
} from "components/common/SimpleTable";
import { Entity } from "lib/api";

const DEFAULT_PAGE_SIZE = 5;

const DEFAULT_SORT = [
  {
    id: "create_timestamp",
    desc: true,
  },
];

interface Event extends Entity, Record<string, unknown> {}

const IncidentEvents: React.FC = () => {
  const { t } = useTranslation("map");

  const columns = useMemo(
    (): HvTableColumnConfig<Event>[] => [
      dateColumn({
        id: "create_timestamp",
        Header: t("sections.events.columns.occurredAt") as string,
        accessor: "timestamp",
        style: { width: 180 },
      }),
      textColumn({
        Header: t("sections.events.columns.name") as string,
        accessor: "name",
        sortType: (rowA, rowB) => {
          const a = rowA.original.name.toLowerCase();

          const b = rowB.original.name.toLowerCase();

          if (a > b) return 1;

          if (b > a) return -1;

          return 0;
        },
        style: { width: "20%" },
      }),
      textColumn({
        Header: t("sections.events.columns.address") as string,
        accessor: "address",
        style: { width: "20%" },
        disableSortBy: true,
      }),
      textColumn({
        Header: t("sections.events.columns.description") as string,
        accessor: "description",
        style: { width: "30%" },
        disableSortBy: true,
      }),
      textColumn({
        Header: t("sections.events.columns.sourceId") as string,
        accessor: "source_id",
        style: { width: 200 },
        disableSortBy: true,
      }),
    ],
    [t]
  );

  const { incident: { hv_correlated_entities = [], hv_entitys = [] } = {} } =
    useContext(IncidentContext);

  const initialState = useMemo(
    () => ({
      pageIndex: DEFAULT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
      sortBy: DEFAULT_SORT,
    }),
    []
  );

  const instance = useHvTable<Event>(
    {
      columns,
      data: hv_entitys,
      getRowId: useCallback((v: Event): string => `${v.id}`, []),
      pageCount: Math.ceil(
        (hv_entitys.length ?? 0) / (initialState?.pageSize ?? 1)
      ),
      initialState,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },
    useHvSortBy,
    useHvPagination
  );

  return (
    <HvGrid container>
      <HvGrid item xs={12}>
        <HvGlobalActions title={t("sections.events.title")} variant="section" />
        <HvTypography variant="normalText">
          {t("sections.events.description")}
        </HvTypography>
      </HvGrid>
      <HvGrid item xs={12}>
        <SimpleTable
          instance={instance}
          totalCount={hv_correlated_entities.length}
          disableSelection
          defaultPageSize={DEFAULT_PAGE_SIZE}
        />
      </HvGrid>
    </HvGrid>
  );
};

export default IncidentEvents;
